import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useTranslation } from '../i18n/useTranslation';
import { API_BASE_URL as API_URL } from '../services/api';
import RouteWithDetour from '../components/RouteWithDetour';
import { HeaderBar, Button } from '../components/ui';
import { motion } from 'framer-motion';
import './MyRides.css';

const BusRouteVisualizer = lazy(() => import('../components/BusRouteVisualizer'));

function MyRides() {
  const navigate = useNavigate();
  const { showToast, isAuthenticated, setPendingRequestsCount, setNewConfirmationsCount } = useApp();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('driving');
  const [myOffers, setMyOffers] = useState([]);
  const [myJoinedRides, setMyJoinedRides] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [myRideRequests, setMyRideRequests] = useState([]); // Ride requests I created (needing a ride)
  const [loading, setLoading] = useState(true);
  const [expandedOffer, setExpandedOffer] = useState(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Edit modal states (kept for backward compatibility but modal is no longer used)
  // eslint-disable-next-line no-unused-vars
  const [showEditModal, setShowEditModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [editingItem, setEditingItem] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [editType, setEditType] = useState(null); // 'offer' or 'request'
  // eslint-disable-next-line no-unused-vars
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    email: '',
    total_seats: '',
    description: '',
    trip_type: 'to_event',
    pickup_address: '',
    pickup_lat: null,
    pickup_lng: null,
    vehicle_type: 'car',
    passenger_count: 1
  });

  // Fetch event details by event_id
  const fetchEventDetails = async (eventId) => {
    try {
      const savedEventData = localStorage.getItem('eventData');
      if (savedEventData) {
        const parsed = JSON.parse(savedEventData);
        if (parsed.eventId === eventId) {
          return parsed;
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  // Fetch all my offers with passenger details
  const fetchMyOffers = useCallback(async () => {
    if (!isAuthenticated) {
      setMyOffers([]);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setMyOffers([]);
        return;
      }

      const response = await fetch(`${API_URL}/auth/my-offers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        setMyOffers([]);
        return;
      }

      const data = await response.json();
      const offersWithDetails = [];

      for (const offer of (data.offers || [])) {
        try {
          const requestsRes = await fetch(`${API_URL}/carpool/offer/${offer.offer_id}/join-requests`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const requestsData = await requestsRes.json();
          let eventDetails = await fetchEventDetails(offer.event_id);
          
          // If eventDetails doesn't have coordinates, try to fetch from API
          if (eventDetails && (!eventDetails.location_lat || !eventDetails.location_lng) && offer.event_code) {
            try {
              const eventResponse = await fetch(`${API_URL}/event/${offer.event_code}`);
              if (eventResponse.ok) {
                const eventData = await eventResponse.json();
                eventDetails = {
                  ...eventDetails,
                  eventLocation: eventData.event_location || eventDetails.eventLocation,
                  location_lat: eventData.location_lat || eventDetails.location_lat,
                  location_lng: eventData.location_lng || eventDetails.location_lng
                };
              }
            } catch (err) {
              console.warn('Could not fetch event coordinates for offer', offer.offer_id);
            }
          }

          offersWithDetails.push({
            ...offer,
            joinRequests: requestsData.requests || [],
            confirmedPassengers: (requestsData.requests || []).filter(r => r.status === 'confirmed'),
            pendingPassengers: (requestsData.requests || []).filter(r => r.status === 'pending'),
            eventDetails
          });
        } catch {
          // Continue with other offers if one fails
        }
      }

      setMyOffers(offersWithDetails);
    } catch {
      setMyOffers([]);
    }
  }, [isAuthenticated]);

  // Fetch rides I've joined as a passenger
  const fetchMyJoinedRides = useCallback(async () => {
    if (!isAuthenticated) {
      setMyJoinedRides([]);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setMyJoinedRides([]);
        return;
      }

      const response = await fetch(`${API_URL}/auth/my-joined-rides`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        setMyJoinedRides([]);
        return;
      }

      const data = await response.json();

      const rides = (data.rides || []).map(ride => ({
        join_request_id: ride.join_request_id,
        offer_id: ride.offer_id,
        status: ride.status,
        driverName: ride.driver_name,
        driverPhone: ride.driver_phone,
        driverEmail: ride.driver_email,
        pickupLocation: ride.pickup_location,
        pickupLat: ride.pickup_lat,
        pickupLng: ride.pickup_lng,
        message: ride.message,
        eventName: ride.event_name,
        eventDate: ride.event_date,
        eventTime: ride.event_time,
        eventLocation: ride.event_location,
        eventLat: ride.event_lat || ride.location_lat || null,
        eventLng: ride.event_lng || ride.location_lng || null,
        eventCode: ride.event_code,
        eventId: ride.event_id,
        offerLocations: ride.offer_locations,
        totalSeats: ride.total_seats,
        availableSeats: ride.available_seats,
        tripType: ride.trip_type,
        created_at: ride.created_at,
        confirmed_at: ride.confirmed_at
      }));
      
      // If any ride is missing event coordinates, try to fetch them from event details
      for (const ride of rides) {
        if (ride.eventCode && !ride.eventLat && !ride.eventLng && ride.eventLocation) {
          try {
            const eventResponse = await fetch(`${API_URL}/event/${ride.eventCode}`);
            if (eventResponse.ok) {
              const eventData = await eventResponse.json();
              if (eventData.location_lat && eventData.location_lng) {
                ride.eventLat = eventData.location_lat;
                ride.eventLng = eventData.location_lng;
              }
            }
          } catch (err) {
            console.warn('Could not fetch event coordinates for', ride.eventCode);
          }
        }
      }

      setMyJoinedRides(rides);
    } catch {
      setMyJoinedRides([]);
    }
  }, [isAuthenticated]);

  // Fetch join requests I've sent (all statuses)
  const fetchMyRequests = useCallback(async () => {
    if (!isAuthenticated) {
      setMyRequests([]);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setMyRequests([]);
        return;
      }

      const response = await fetch(`${API_URL}/auth/my-join-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        setMyRequests([]);
        return;
      }

      const data = await response.json();
      setMyRequests(data.requests || []);
    } catch {
      setMyRequests([]);
    }
  }, [isAuthenticated]);

  // Fetch my ride requests (when I need a ride)
  const fetchMyRideRequests = useCallback(async () => {
    if (!isAuthenticated) {
      setMyRideRequests([]);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setMyRideRequests([]);
        return;
      }

      const response = await fetch(`${API_URL}/auth/my-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        setMyRideRequests([]);
        return;
      }

      const data = await response.json();
      setMyRideRequests(data.requests || []);
    } catch {
      setMyRideRequests([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchMyOffers(), fetchMyJoinedRides(), fetchMyRequests(), fetchMyRideRequests()]);
      setLoading(false);
    };
    if (isAuthenticated !== undefined) {
      loadData();
    }
  }, [fetchMyOffers, fetchMyJoinedRides, fetchMyRequests, fetchMyRideRequests, isAuthenticated]);

  // Calculate total pending requests from all offers for header badge
  const totalPendingRequests = myOffers.reduce((sum, offer) => sum + (offer.pendingPassengers?.length || 0), 0);
  
  // Calculate new confirmations (requests that were pending and are now confirmed)
  // These are requests I made that have been confirmed by a driver
  const newConfirmations = myRequests.filter(r => {
    // Check if this request was recently confirmed (within last 24 hours)
    if (r.status === 'confirmed' && r.confirmed_at) {
      const confirmedDate = new Date(r.confirmed_at);
      const now = new Date();
      const hoursSinceConfirmation = (now - confirmedDate) / (1000 * 60 * 60);
      // Consider it "new" if confirmed within last 24 hours
      return hoursSinceConfirmation < 24;
    }
    return false;
  }).length;
  
  // Update AppContext with pending requests count and new confirmations
  useEffect(() => {
    setPendingRequestsCount(totalPendingRequests);
    setNewConfirmationsCount(newConfirmations);
  }, [totalPendingRequests, newConfirmations, setPendingRequestsCount, setNewConfirmationsCount]);

  const handleAcceptPassenger = async (offerId, requestId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showToast(t('myRides.pleaseLogin'), 'error');
        return;
      }
      
      const response = await fetch(`${API_URL}/carpool/offer/${offerId}/accept-join/${requestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        showToast(t('myRides.passengerConfirmed'), 'success');
        await fetchMyOffers();
      } else {
        const data = await response.json();
        showToast(data.message || t('myRides.failedToAccept'), 'error');
      }
    } catch (error) {
      console.error('Error accepting passenger:', error);
      showToast(t('myRides.failedToAccept'), 'error');
    }
  };

  const handleRejectPassenger = async (offerId, requestId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showToast(t('myRides.pleaseLogin'), 'error');
        return;
      }
      
      const response = await fetch(`${API_URL}/carpool/offer/${offerId}/reject-join/${requestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        showToast(t('myRides.requestDeclined'), 'success');
        await fetchMyOffers();
      } else {
        const data = await response.json();
        showToast(data.message || t('myRides.failedToDecline'), 'error');
      }
    } catch (error) {
      console.error('Error rejecting passenger:', error);
      showToast(t('myRides.failedToDecline'), 'error');
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm(t('myRides.confirmCancelRequest'))) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/carpool/join-request/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        showToast(t('myRides.requestCancelled'), 'success');
        await fetchMyRequests();
        await fetchMyJoinedRides();
      } else {
        showToast(t('myRides.failedToCancelRequest'), 'error');
      }
    } catch {
      showToast(t('myRides.failedToCancelRequest'), 'error');
    }
  };

  // Cancel a ride request I created (when I need a ride)
  const handleCancelRideRequest = async (requestId) => {
    if (!window.confirm(t('myRides.confirmCancelRideRequest') || 'Are you sure you want to cancel this ride request?')) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/carpool/request/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        showToast(t('myRides.rideRequestCancelled') || 'Ride request cancelled', 'success');
        await fetchMyRideRequests();
      } else {
        showToast(t('myRides.failedToCancelRideRequest') || 'Failed to cancel ride request', 'error');
      }
    } catch {
      showToast(t('myRides.failedToCancelRideRequest') || 'Failed to cancel ride request', 'error');
    }
  };

  // Delete an offer (as driver)
  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm(t('myRides.confirmDeleteOffer'))) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/carpool/offer/${offerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        showToast(t('myRides.offerDeleted'), 'success');
        setMyOffers(myOffers.filter(o => o.offer_id !== offerId));
      } else {
        const data = await response.json();
        showToast(data.message || t('myRides.failedToDeleteOffer'), 'error');
      }
    } catch {
      showToast(t('myRides.failedToDeleteOffer'), 'error');
    }
  };

  // Navigate to PublishRidePage for editing an offer
  const handleEditOffer = (offer) => {
    // Store edit data in localStorage and navigate to PublishRidePage
    const editData = {
      type: 'offer',
      item: offer,
      editId: offer.offer_id
    };
    localStorage.setItem('editRideData', JSON.stringify(editData));
    
    // Navigate to publish page with edit mode
    const eventCode = offer.event_code;
    if (eventCode) {
      navigate(`/event/${eventCode}/publish?mode=offer&edit=true`);
    } else {
      showToast(t('myRides.eventNotFound') || 'Event not found', 'error');
    }
  };

  // Navigate to PublishRidePage for editing a request
  const handleEditRequest = (request) => {
    // Store edit data in localStorage and navigate to PublishRidePage
    const editData = {
      type: 'request',
      item: request,
      editId: request.request_id
    };
    localStorage.setItem('editRideData', JSON.stringify(editData));
    
    // Navigate to publish page with edit mode
    const eventCode = request.event_code;
    if (eventCode) {
      navigate(`/event/${eventCode}/publish?mode=request&edit=true`);
    } else {
      showToast(t('myRides.eventNotFound') || 'Event not found', 'error');
    }
  };

  // Save edited item
  const handleSaveEdit = async () => {
    if (!editingItem) return;

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showToast(t('myRides.pleaseLogin'), 'error');
        return;
      }

      const endpoint = editType === 'offer' 
        ? `${API_URL}/carpool/offer/${editingItem.offer_id}`
        : `${API_URL}/carpool/request/${editingItem.request_id}`;

      const payload = editType === 'offer' 
        ? {
            name: editFormData.name,
            phone: editFormData.phone,
            email: editFormData.email,
            total_seats: parseInt(editFormData.total_seats) || 1,
            description: editFormData.description,
            trip_type: editFormData.trip_type,
            locations: [{
              location_address: editFormData.pickup_address,
              location_lat: editFormData.pickup_lat,
              location_lng: editFormData.pickup_lng,
              trip_direction: 'going'
            }]
          }
        : {
            name: editFormData.name,
            phone: editFormData.phone,
            email: editFormData.email,
            trip_type: editFormData.trip_type,
            passenger_count: parseInt(editFormData.passenger_count) || 1,
            locations: [{
              location_address: editFormData.pickup_address,
              location_lat: editFormData.pickup_lat,
              location_lng: editFormData.pickup_lng,
              trip_direction: 'going'
            }]
          };

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showToast(t('myRides.updateSuccess') || 'Updated successfully!', 'success');
        setShowEditModal(false);
        setEditingItem(null);
        // Refresh data
        if (editType === 'offer') {
          await fetchMyOffers();
        } else {
          await fetchMyRideRequests();
        }
      } else {
        const data = await response.json();
        showToast(data.message || t('myRides.updateFailed') || 'Update failed', 'error');
      }
    } catch (error) {
      console.error('Error updating:', error);
      showToast(t('myRides.updateFailed') || 'Update failed', 'error');
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="my-rides-page">
        <HeaderBar />
        <div className="rides-container">
          <div className="rides-header-section">
            <div className="skeleton-title"></div>
            <div className="skeleton-subtitle"></div>
          </div>
          <div className="skeleton-tabs">
            <div className="skeleton-tab"></div>
            <div className="skeleton-tab"></div>
          </div>
          <div className="skeleton-cards">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton-card">
                <div className="skeleton-line title"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line short"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Calculate counts for tabs
  const confirmedIds = new Set(myJoinedRides.map(r => r.join_request_id || r.id));
  const nonDuplicateRequests = myRequests.filter(r => r.status !== 'confirmed' || !confirmedIds.has(r.join_request_id || r.id));
  const passengerTotalCount = myJoinedRides.length + nonDuplicateRequests.length;
  const pendingCount = myRequests.filter(r => r.status === 'pending').length;

  return (
    <div className="my-rides-page">
      <HeaderBar />
      
      <div className="rides-container">
        {/* Page Header */}
        <motion.div 
          className="rides-header-section"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1>{t('myRides.title')}</h1>
          <p className="rides-subtitle">{t('myRides.subtitle')}</p>
        </motion.div>

        {/* Tabs */}
        <motion.div 
          className="rides-tabs"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <button
            className={`tab-btn ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            <span className="tab-icon">📝</span>
            <span className="tab-label">{t('myRides.myPosts') || 'منشوراتي'}</span>
            {(myOffers.length + myRideRequests.length) > 0 && <span className="tab-count">{myOffers.length + myRideRequests.length}</span>}
          </button>
          <button
            className={`tab-btn ${activeTab === 'driving' ? 'active' : ''}`}
            onClick={() => setActiveTab('driving')}
          >
            <span className="tab-icon">🚗</span>
            <span className="tab-label">{t('myRides.imDriving')}</span>
            {myOffers.length > 0 && <span className="tab-count">{myOffers.length}</span>}
          </button>
          <button
            className={`tab-btn ${activeTab === 'passenger' ? 'active' : ''}`}
            onClick={() => setActiveTab('passenger')}
          >
            <span className="tab-icon">🎫</span>
            <span className="tab-label">{t('myRides.imPassenger')}</span>
            {passengerTotalCount > 0 && <span className="tab-count">{passengerTotalCount}</span>}
            {pendingCount > 0 && <span className="tab-count pending">{pendingCount}</span>}
          </button>
        </motion.div>

        {/* Content */}
        <motion.div 
          className="rides-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          {activeTab === 'posts' ? (
            /* MY POSTS TAB - All published offers and requests */
            <div className="posts-section">
              {(myOffers.length === 0 && myRideRequests.length === 0) ? (
                <div className="empty-state">
                  <div className="empty-icon">📝</div>
                  <h3>{t('myRides.noPosts') || 'لا توجد منشورات'}</h3>
                  <p>{t('myRides.createPost') || 'قم بنشر عرض رحلة أو طلب رحلة'}</p>
                  <Button variant="primary" onClick={() => navigate('/')}>
                    {t('myRides.browseEvents')}
                  </Button>
                </div>
              ) : (
                <div className="posts-list">
                  {/* My Offers Section */}
                  {myOffers.length > 0 && (
                    <div className="posts-group">
                      <h3 className="posts-group-title">🚗 {t('myRides.myOffers') || 'عروض الرحلات'} ({myOffers.length})</h3>
                      {myOffers.map((offer) => (
                        <motion.div 
                          key={`offer-${offer.offer_id}`} 
                          className="post-card offer"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          whileHover={{ y: -2 }}
                        >
                          <div className="post-header">
                            <span className="post-type-badge offer">🚗 {t('myRides.rideOffer') || 'عرض رحلة'}</span>
                            <span className="post-event">{offer.event_name || 'Event'}</span>
                          </div>
                          
                          <div className="post-details">
                            <div className="post-detail">
                              <span className="detail-icon">📅</span>
                              <span className="detail-text">
                                {offer.event_date ? new Date(offer.event_date).toLocaleDateString('ar-EG', {
                                  weekday: 'short', month: 'short', day: 'numeric'
                                }) : '-'}
                              </span>
                            </div>
                            {offer.locations?.[0] && (
                              <div className="post-detail">
                                <span className="detail-icon">📍</span>
                                <span className="detail-text">{offer.locations[0].nickname || offer.locations[0].location_address?.split(',')[0]}</span>
                              </div>
                            )}
                            <div className="post-detail">
                              <span className="detail-icon">💺</span>
                              <span className="detail-text">
                                {offer.confirmedPassengers?.length || 0} / {offer.total_seats || 0} {t('eventDashboard.seats') || 'مقاعد'}
                              </span>
                            </div>
                            {offer.pendingPassengers?.length > 0 && (
                              <div className="post-detail pending">
                                <span className="detail-icon">⏳</span>
                                <span className="detail-text">{offer.pendingPassengers.length} {t('myRides.pendingRequests') || 'طلبات معلقة'}</span>
                              </div>
                            )}
                          </div>

                          <div className="post-actions">
                            {offer.event_code && (
                              <button className="post-btn view" onClick={() => navigate(`/event/${offer.event_code}`)}>
                                👁️ {t('myRides.viewEvent') || 'عرض'}
                              </button>
                            )}
                            <button className="post-btn edit" onClick={() => handleEditOffer(offer)}>
                              ✏️ {t('common.edit') || 'تعديل'}
                            </button>
                            <button className="post-btn delete" onClick={() => handleDeleteOffer(offer.offer_id)}>
                              🗑️
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* My Requests Section */}
                  {myRideRequests.length > 0 && (
                    <div className="posts-group">
                      <h3 className="posts-group-title">🙋 {t('myRides.myRideRequests') || 'طلبات الرحلات'} ({myRideRequests.length})</h3>
                      {myRideRequests.map((request) => (
                        <motion.div 
                          key={`request-${request.request_id}`} 
                          className={`post-card request ${request.matched_driver ? 'matched' : ''}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          whileHover={{ y: -2 }}
                        >
                          <div className="post-header">
                            <span className="post-type-badge request">🙋 {t('myRides.rideRequest') || 'طلب رحلة'}</span>
                            <span className="post-event">{request.event_name || 'Event'}</span>
                            {request.matched_driver && (
                              <span className="matched-badge">✅ {t('myRides.matched') || 'تم التوصيل'}</span>
                            )}
                          </div>
                          
                          <div className="post-details">
                            <div className="post-detail">
                              <span className="detail-icon">📅</span>
                              <span className="detail-text">
                                {request.event_date ? new Date(request.event_date).toLocaleDateString('ar-EG', {
                                  weekday: 'short', month: 'short', day: 'numeric'
                                }) : '-'}
                              </span>
                            </div>
                            {request.locations?.[0] && (
                              <div className="post-detail">
                                <span className="detail-icon">📍</span>
                                <span className="detail-text">{request.locations[0].nickname || request.locations[0].location_address?.split(',')[0]}</span>
                              </div>
                            )}
                            <div className="post-detail">
                              <span className="detail-icon">👥</span>
                              <span className="detail-text">{request.passenger_count || 1} {t('publishRide.passengers') || 'ركاب'}</span>
                            </div>
                            {request.matched_driver && (
                              <div className="post-detail driver">
                                <span className="detail-icon">🚗</span>
                                <span className="detail-text">{request.matched_driver.name}</span>
                              </div>
                            )}
                          </div>

                          <div className="post-actions">
                            {request.event_code && (
                              <button className="post-btn view" onClick={() => navigate(`/event/${request.event_code}`)}>
                                👁️ {t('myRides.viewEvent') || 'عرض'}
                              </button>
                            )}
                            <button className="post-btn edit" onClick={() => handleEditRequest(request)}>
                              ✏️ {t('common.edit') || 'تعديل'}
                            </button>
                            {!request.matched_driver && (
                              <button className="post-btn delete" onClick={() => handleCancelRideRequest(request.request_id)}>
                                🗑️
                              </button>
                            )}
                            {request.matched_driver && (
                              <a 
                                href={`https://wa.me/${request.matched_driver.phone?.replace(/\D/g, '')}`} 
                                className="post-btn whatsapp"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                </svg>
                              </a>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : activeTab === 'driving' ? (
            /* DRIVING TAB */
            <div className="driving-section">
              {myOffers.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🚗</div>
                  <h3>{t('myRides.noRidesOffered')}</h3>
                  <p>{t('myRides.shareYourCar')}</p>
                  <Button variant="primary" onClick={() => navigate('/')}>
                    {t('myRides.browseEvents')}
                  </Button>
                </div>
              ) : (
                <div className="offers-list">
                  {myOffers.map((offer) => (
                    <motion.div 
                      key={offer.offer_id} 
                      className="offer-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ y: -2 }}
                    >
                      {/* Event Header */}
                      <div className="offer-event-header">
                        <div className="event-info">
                          <span className="event-badge">📅 {offer.event_name || 'Event'}</span>
                          {offer.event_date && (
                            <span className="event-date">
                              {new Date(offer.event_date).toLocaleDateString('en-GB', {
                                weekday: 'short', month: 'short', day: 'numeric'
                              })}
                            </span>
                          )}
                        </div>
                        <div className="seats-badge">
                          <span className="seats-count">
                            {offer.confirmedPassengers?.reduce((sum, p) => sum + (parseInt(p.passenger_count) || 1), 0) || 0}
                          </span>
                          <span className="seats-total">/ {offer.total_seats}</span>
                          {offer.pendingPassengers?.length > 0 && (
                            <span className="pending-indicator">{offer.pendingPassengers.length} pending</span>
                          )}
                        </div>
                      </div>

                      {/* Route Preview */}
                      {offer.locations && offer.locations.length > 0 && (
                        <div className="route-preview">
                          {offer.vehicle_type === 'public' ? (
                            // Bus route with multiple stops
                            (() => {
                              const pickups = offer.locations.filter(l => l.location_type === 'pickup' || l.trip_direction === 'going' || !l.location_type);
                              const dropoffs = offer.locations.filter(l => l.location_type === 'dropoff' || l.trip_direction === 'return');
                              
                              const formatLocation = (loc) => {
                                if (loc.nickname) return loc.nickname;
                                if (loc.location_address && loc.location_address.length < 50) return loc.location_address;
                                return loc.location_address?.split(',')[0] || 'Location on map';
                              };

                              const eventName = offer.eventDetails?.eventName || offer.event_name || offer.eventName || 'Event';
                              return (
                                <>
                                  {pickups.length > 0 && (
                                    <div className="bus-route-preview">
                                      <span className="route-direction">→ {t('myRides.going') || 'Going'}</span>
                                      <div className="bus-route-stops">
                                        <span className="route-start">{formatLocation(pickups[0])}</span>
                                        {/* Show all middle stops */}
                                        {pickups.length > 1 && pickups.slice(1).map((stop, idx) => (
                                          <React.Fragment key={idx}>
                                            <span className="route-arrow">→</span>
                                            <span className="route-stop">{formatLocation(stop)}</span>
                                          </React.Fragment>
                                        ))}
                                        {/* Always show event as final destination for going route */}
                                        <span className="route-arrow">→</span>
                                        <span className="route-endpoint">🎯 {eventName}</span>
                                      </div>
                                    </div>
                                  )}
                                  {dropoffs.length > 0 && (
                                    <div className="bus-route-preview">
                                      <span className="route-direction">← {t('myRides.return') || 'Return'}</span>
                                      <div className="bus-route-stops">
                                        {/* Event is the starting point for return route */}
                                        <span className="route-start">🎯 {eventName}</span>
                                        {/* Show all dropoff stops */}
                                        {dropoffs.map((stop, idx) => (
                                          <React.Fragment key={idx}>
                                            <span className="route-arrow">→</span>
                                            <span className={idx === dropoffs.length - 1 ? 'route-endpoint' : 'route-stop'}>{formatLocation(stop)}</span>
                                          </React.Fragment>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()
                          ) : (
                            // Regular car route (simple start → end)
                            <>
                          <div className="route-point start">
                            <div className="point-dot start"></div>
                            <span>{offer.locations[0]?.location_address}</span>
                          </div>
                          <div className="route-line"></div>
                          <div className="route-point end">
                            <div className="point-dot end"></div>
                            <span>{offer.eventDetails?.eventLocation || offer.event_location || 'Event'}</span>
                          </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Confirmed Passengers */}
                      {offer.confirmedPassengers.length > 0 && (
                        <div className="passengers-section confirmed">
                          <h4>✅ {t('myRides.confirmed')} ({offer.confirmedPassengers.length})</h4>
                          <div className="passengers-grid">
                            {offer.confirmedPassengers.map((passenger, idx) => (
                              <div key={idx} className="passenger-chip">
                                <div className="passenger-avatar">
                                  {passenger.gender === 'female' ? '👩' : passenger.gender === 'male' ? '👨' : passenger.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div className="passenger-info">
                                  <strong>{passenger.name}</strong>
                                  <span className="pickup">📍 {passenger.pickup_location || 'No location'}</span>
                                </div>
                                <div className="passenger-actions">
                                  <a href={`https://wa.me/${passenger.phone?.replace(/\D/g, '')}`} className="action-btn whatsapp" title="WhatsApp">💬</a>
                                  <a href={`tel:${passenger.phone}`} className="action-btn call" title="Call">📞</a>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Pending Requests */}
                      {offer.pendingPassengers.length > 0 && (
                        <div className="passengers-section pending">
                          <h4>⏳ {t('myRides.pendingRequests')} ({offer.pendingPassengers.length})</h4>
                          <div className="passengers-grid">
                            {offer.pendingPassengers.map((passenger, idx) => {
                              const passengerCount = parseInt(passenger.passenger_count) || 1;
                              return (
                              <div key={idx} className="passenger-chip pending">
                                <div className="passenger-avatar pending">
                                  {passenger.gender === 'female' ? '👩' : passenger.gender === 'male' ? '👨' : passenger.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div className="passenger-info">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <strong>{passenger.name}</strong>
                                      {passengerCount > 1 && (
                                        <span style={{ 
                                          fontSize: '13px', 
                                          fontWeight: 700, 
                                          color: '#059669', 
                                          padding: '3px 10px', 
                                          background: '#d1fae5', 
                                          borderRadius: '6px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px'
                                        }}>
                                          👥 {passengerCount} {passengerCount === 1 ? 'passenger' : 'passengers'}
                                        </span>
                                      )}
                                    </div>
                                  <span className="pickup">📍 {passenger.pickup_location || t('myRides.noLocation')}</span>
                                  {passenger.message && <span className="message">💬 {passenger.message}</span>}
                                </div>
                                <div className="request-actions">
                                  <button className="accept-btn" onClick={() => handleAcceptPassenger(offer.offer_id, passenger.id)}>
                                    ✅ {t('myRides.accept')}
                                  </button>
                                  <button className="decline-btn" onClick={() => handleRejectPassenger(offer.offer_id, passenger.id)}>
                                    ✕
                                  </button>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Expand for Map */}
                      <div className="offer-actions-bar">
                        <button
                          className="expand-btn"
                          onClick={() => setExpandedOffer(expandedOffer === offer.offer_id ? null : offer.offer_id)}
                        >
                          {expandedOffer === offer.offer_id ? '▲ ' + t('myRides.hideMap') : '🗺️ ' + t('myRides.showRoute')}
                        </button>
                        <div className="action-buttons">
                          {offer.event_code && (
                            <button className="view-event-btn" onClick={() => navigate(`/event/${offer.event_code}`)}>
                              {t('myRides.viewEvent')}
                            </button>
                          )}
                          <button className="edit-btn" onClick={() => handleEditOffer(offer)} title={t('myRides.edit') || 'Edit'}>
                            ✏️
                          </button>
                          <button className="delete-btn" onClick={() => handleDeleteOffer(offer.offer_id)}>
                            🗑️
                          </button>
                        </div>
                      </div>

                      {/* Expanded Map */}
                      {expandedOffer === offer.offer_id && (
                        <motion.div 
                          className="expanded-map"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          {offer.vehicle_type === 'public' ? (
                            // Bus route with multiple stops
                            <Suspense fallback={
                              <div className="no-map-message">
                                <p>Loading map...</p>
                              </div>
                            }>
                              <BusRouteVisualizer
                                pickupStops={offer.locations
                                  ?.filter(l => l.location_type === 'pickup' || l.trip_direction === 'going' || !l.location_type)
                                  .map(l => ({
                                    address: l.location_address,
                                    lat: l.location_lat || null,
                                    lng: l.location_lng || null,
                                    nickname: l.nickname,
                                    time: l.stop_time
                                  }))
                                  .filter(s => s.lat && s.lng) || []}
                                dropoffStops={offer.locations
                                  ?.filter(l => l.location_type === 'dropoff' || l.trip_direction === 'return')
                                  .map(l => ({
                                    address: l.location_address,
                                    lat: l.location_lat || null,
                                    lng: l.location_lng || null,
                                    nickname: l.nickname,
                                    time: l.stop_time
                                  }))
                                  .filter(s => s.lat && s.lng) || []}
                                eventLocation={offer.eventDetails?.location_lat && offer.eventDetails?.location_lng
                                  ? { lat: parseFloat(offer.eventDetails.location_lat), lng: parseFloat(offer.eventDetails.location_lng) }
                                  : null}
                                eventName={offer.eventDetails?.event_name || offer.event_name}
                                height="350px"
                                showLabels={true}
                              />
                            </Suspense>
                          ) : (
                            // Regular car route
                            offer.locations?.[0]?.location_address && offer.confirmedPassengers.length > 0 && (
                              (offer.eventDetails?.location_lat && offer.eventDetails?.location_lng) || 
                              (offer.eventDetails?.eventLocation && offer.eventDetails.eventLocation !== 'Event') ||
                              (offer.event_location && offer.event_location !== 'Event')
                            ) ? (
                            <RouteWithDetour
                              origin={offer.locations[0].location_address}
                                destination={
                                  offer.eventDetails?.location_lat && offer.eventDetails?.location_lng
                                    ? { 
                                        address: offer.eventDetails.eventLocation || offer.eventDetails.event_name || offer.event_location || 'Event',
                                        lat: parseFloat(offer.eventDetails.location_lat),
                                        lng: parseFloat(offer.eventDetails.location_lng)
                                      }
                                    : (offer.eventDetails?.eventLocation && offer.eventDetails.eventLocation !== 'Event') 
                                      ? offer.eventDetails.eventLocation
                                      : (offer.event_location && offer.event_location !== 'Event')
                                        ? offer.event_location
                                        : null
                                }
                              pickupLocations={offer.confirmedPassengers
                                .filter(p => p.pickup_lat != null && p.pickup_lng != null)
                                .map(p => ({
                                  address: p.pickup_location,
                                  lat: parseFloat(p.pickup_lat),
                                  lng: parseFloat(p.pickup_lng),
                                  name: p.name
                                }))}
                              height="350px"
                            />
                          ) : (
                            <div className="no-map-message">
                              <p>{t('myRides.noPassengersForMap')}</p>
                            </div>
                            )
                          )}
                        </motion.div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          ) : activeTab === 'passenger' ? (
            /* PASSENGER TAB */
            <div className="passenger-section">
              {/* Filters */}
              <div className="filter-bar">
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
                  <option value="all">{t('myRides.allStatus')}</option>
                  <option value="confirmed">✅ {t('myRides.confirmed')}</option>
                  <option value="pending">⏳ {t('myRides.pending')}</option>
                  <option value="rejected">❌ {t('myRides.rejected')}</option>
                </select>
                <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="filter-select">
                  <option value="all">{t('myRides.allDates')}</option>
                  <option value="upcoming">📅 {t('myRides.upcoming')}</option>
                  <option value="past">📆 {t('myRides.past')}</option>
                </select>
              </div>

              {(() => {
                // Combine and filter rides
                const allRides = [
                  ...myJoinedRides.map(r => ({ ...r, type: 'joined', sortDate: r.created_at })),
                  ...myRequests
                    .filter(r => r.status !== 'confirmed' || !confirmedIds.has(r.join_request_id || r.id))
                    .map(r => ({ 
                      ...r, 
                      type: 'request', 
                      sortDate: r.created_at,
                      eventName: r.event_name,
                      eventDate: r.event_date,
                      eventTime: r.event_time,
                      eventLocation: r.event_location,
                      eventCode: r.event_code,
                      driverName: r.driver_name,
                      driverPhone: r.driver_phone,
                      pickupLocation: r.pickup_location
                    }))
                ];

                let filtered = allRides;
                if (statusFilter !== 'all') {
                  filtered = filtered.filter(r => r.status === statusFilter);
                }
                if (dateFilter !== 'all') {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  filtered = filtered.filter(r => {
                    if (!r.eventDate) return true;
                    const eventDate = new Date(r.eventDate);
                    return dateFilter === 'upcoming' ? eventDate >= today : eventDate < today;
                  });
                }

                filtered.sort((a, b) => {
                  const statusOrder = { confirmed: 0, pending: 1, rejected: 2 };
                  const statusDiff = (statusOrder[a.status] || 1) - (statusOrder[b.status] || 1);
                  if (statusDiff !== 0) return statusDiff;
                  return new Date(b.sortDate) - new Date(a.sortDate);
                });

                if (filtered.length === 0) {
                  return (
                    <div className="empty-state">
                      <div className="empty-icon">🎫</div>
                      <h3>{t('myRides.noRidesFound')}</h3>
                      <p>{t('myRides.browseEventsToFind')}</p>
                      <Button variant="primary" onClick={() => navigate('/')}>
                        {t('myRides.browseEvents')}
                      </Button>
                    </div>
                  );
                }

                return (
                  <div className="rides-list">
                    {filtered.map((ride, idx) => (
                      <motion.div 
                        key={ride.join_request_id || ride.id || idx} 
                        className={`ride-card ${ride.status}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <div className="ride-card-header">
                          <div className="ride-event">
                            <h3>{ride.eventName || 'Event'}</h3>
                            {ride.eventDate && (
                              <span className="ride-date">
                                {new Date(ride.eventDate).toLocaleDateString('en-GB', {
                                  weekday: 'short', month: 'short', day: 'numeric'
                                })} {ride.eventTime && `• ${ride.eventTime}`}
                              </span>
                            )}
                          </div>
                          <span className={`status-pill ${ride.status}`}>
                            {ride.status === 'confirmed' ? '✅ ' + t('myRides.confirmed') : 
                             ride.status === 'pending' ? '⏳ ' + t('myRides.pending') : 
                             ride.status === 'rejected' ? '❌ ' + t('myRides.rejected') : ride.status}
                          </span>
                        </div>

                        <div className="ride-card-body">
                          <div className="ride-detail">
                            <span className="label">👤 {t('myRides.driver')}</span>
                            <span className="value">{ride.driverName}</span>
                          </div>
                          {ride.status === 'confirmed' && ride.driverPhone && (
                            <div className="ride-detail contact">
                              <span className="label">📱 {t('myRides.contact')}</span>
                              <span className="value">
                                {ride.driverPhone}
                                <a href={`tel:${ride.driverPhone}`} className="contact-btn">📞</a>
                                <a href={`https://wa.me/${ride.driverPhone?.replace(/\D/g, '')}`} className="contact-btn">💬</a>
                              </span>
                            </div>
                          )}
                          {ride.status === 'pending' && (
                            <div className="pending-notice">
                              ⏳ {t('myRides.driverContactPending')}
                            </div>
                          )}
                          {ride.pickupLocation && (
                            <div className="ride-detail">
                              <span className="label">📍 {t('myRides.pickup')}</span>
                              <span className="value">{ride.pickupLocation}</span>
                            </div>
                          )}
                          {ride.eventLocation && (
                            <div className="ride-detail">
                              <span className="label">🎯 {t('myRides.event')}</span>
                              <span className="value">{ride.eventLocation}</span>
                            </div>
                          )}
                        </div>

                        {/* Route Map for Confirmed Rides */}
                        {ride.status === 'confirmed' && ride.offerLocations?.[0]?.location_address && ride.eventLocation && ride.pickupLocation && ride.pickupLat && ride.pickupLng && (() => {
                          const destination = ride.eventLat && ride.eventLng
                            ? { address: ride.eventLocation, lat: parseFloat(ride.eventLat), lng: parseFloat(ride.eventLng) }
                            : ride.eventLocation && ride.eventLocation !== 'Event' ? ride.eventLocation : null;
                          const pickupLocation = {
                            address: ride.pickupLocation,
                            lat: parseFloat(ride.pickupLat),
                            lng: parseFloat(ride.pickupLng)
                          };
                          return (
                            <div className="ride-map" key={`map-${ride.join_request_id || ride.id}`}>
                            <RouteWithDetour
                                key={`route-${ride.join_request_id || ride.id}-${ride.eventLat}-${ride.eventLng}-${ride.pickupLat}-${ride.pickupLng}`}
                              origin={ride.offerLocations[0].location_address}
                                destination={destination}
                                pickupLocation={pickupLocation}
                              passengerName={ride.passengerName || ride.name}
                              height="250px"
                            />
                          </div>
                          );
                        })()}

                        <div className="ride-card-actions">
                          {ride.eventCode && (
                            <button className="action-btn secondary" onClick={() => navigate(`/event/${ride.eventCode}`)}>
                              {t('myRides.viewEvent')}
                            </button>
                          )}
                          {(ride.status === 'pending' || ride.status === 'confirmed') && (
                            <button className="action-btn cancel" onClick={() => handleCancelRequest(ride.join_request_id || ride.id)}>
                              {ride.status === 'pending' ? t('myRides.cancelRequest') : t('myRides.cancelRide')}
                            </button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                );
              })()}
            </div>
          ) : null}
        </motion.div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="edit-modal-overlay" onClick={() => setShowEditModal(false)}>
          <motion.div 
            className="edit-modal"
            onClick={e => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <div className="edit-modal-header">
              <h2>
                {editType === 'offer' 
                  ? (t('myRides.editOffer') || '✏️ Edit Ride Offer')
                  : (t('myRides.editRequest') || '✏️ Edit Ride Request')
                }
              </h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            
            <div className="edit-modal-body">
              <div className="edit-form">
                <div className="form-group">
                  <label>{t('publishRide.yourName') || 'Your Name'}</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    placeholder={t('publishRide.namePlaceholder') || 'Enter your name'}
                  />
                </div>

                <div className="form-group">
                  <label>{t('publishRide.phone') || 'Phone Number'}</label>
                  <input
                    type="tel"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                    placeholder={t('publishRide.phonePlaceholder') || 'Enter phone number'}
                  />
                </div>

                <div className="form-group">
                  <label>{t('publishRide.email') || 'Email'} ({t('common.optional') || 'Optional'})</label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    placeholder={t('publishRide.emailPlaceholder') || 'Enter email'}
                  />
                </div>

                <div className="form-group">
                  <label>{t('publishRide.pickupLocation') || 'Pickup Location'}</label>
                  <input
                    type="text"
                    value={editFormData.pickup_address}
                    onChange={(e) => setEditFormData({...editFormData, pickup_address: e.target.value})}
                    placeholder={t('publishRide.pickupPlaceholder') || 'Enter pickup address'}
                  />
                </div>

                {editType === 'offer' && (
                  <>
                    <div className="form-group">
                      <label>{t('publishRide.seats') || 'Available Seats'}</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={editFormData.total_seats}
                        onChange={(e) => setEditFormData({...editFormData, total_seats: e.target.value})}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('publishRide.description') || 'Description'} ({t('common.optional') || 'Optional'})</label>
                      <textarea
                        value={editFormData.description}
                        onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                        placeholder={t('publishRide.descriptionPlaceholder') || 'Add any notes'}
                        rows="3"
                      />
                    </div>
                  </>
                )}

                {editType === 'request' && (
                  <div className="form-group">
                    <label>{t('publishRide.passengerCount') || 'Number of Passengers'}</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={editFormData.passenger_count}
                      onChange={(e) => setEditFormData({...editFormData, passenger_count: e.target.value})}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{t('publishRide.tripType') || 'Trip Type'}</label>
                  <select
                    value={editFormData.trip_type}
                    onChange={(e) => setEditFormData({...editFormData, trip_type: e.target.value})}
                  >
                    <option value="to_event">{t('publishRide.toEvent') || '➡️ To Event'}</option>
                    <option value="from_event">{t('publishRide.fromEvent') || '⬅️ From Event'}</option>
                    <option value="round_trip">{t('publishRide.roundTrip') || '↔️ Round Trip'}</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="edit-modal-footer">
              <button className="cancel-btn" onClick={() => setShowEditModal(false)}>
                {t('common.cancel') || 'Cancel'}
              </button>
              <button className="save-btn" onClick={handleSaveEdit}>
                {t('common.save') || 'Save Changes'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default MyRides;
